import { IntlProvider } from "use-intl";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import enUS from "../messages/en-US.json";
import jaJP from "../messages/ja-JP.json";
import { resolveBrowserTimeZone } from "./lib/session-date-groups";

export type AppLocale = "en-US" | "ja-JP";

const localeStorageKey = "flowtrace.locale";
const messages = { "en-US": enUS, "ja-JP": jaJP } as const;

export const resolveLocale = (
  storedLocale: string | null | undefined,
  browserLanguage: string | null | undefined,
): AppLocale => {
  if (storedLocale === "en-US" || storedLocale === "ja-JP") {
    return storedLocale;
  }
  return browserLanguage?.toLowerCase().startsWith("ja")
    ? "ja-JP"
    : "en-US";
};

const resolveInitialLocale = (): AppLocale => {
  const requestedLocale = new URLSearchParams(window.location.search).get("lang");
  if (requestedLocale === "ja" || requestedLocale === "ja-JP") return "ja-JP";
  if (requestedLocale === "en" || requestedLocale === "en-US") return "en-US";
  let storedLocale: string | null = null;
  try {
    storedLocale = window.localStorage.getItem(localeStorageKey);
  } catch {
    // A browser can disable storage while still allowing the application to run.
  }
  return resolveLocale(storedLocale, window.navigator.language);
};

interface LocaleContextValue {
  locale: AppLocale;
  setLocale(locale: AppLocale): void;
  timeZone: string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const FlowTraceIntlProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<AppLocale>(resolveInitialLocale);
  const [timeZone] = useState(resolveBrowserTimeZone);
  const setLocale = useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(localeStorageKey, locale);
    } catch {
      // Locale switching should still work when persistence is unavailable.
    }
  }, [locale]);

  const contextValue = useMemo(
    () => ({ locale, setLocale, timeZone }),
    [locale, setLocale, timeZone],
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider
        locale={locale}
        messages={messages[locale]}
        timeZone={timeZone}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
};

export const useAppLocale = (): LocaleContextValue => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useAppLocale must be used inside FlowTraceIntlProvider");
  return context;
};
